import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Plane, Users, Clock, AlertCircle, Sparkles, TrendingUp, Route } from "lucide-react";

const STATUS_PILL = {
  Submitted: "as-pill-blue", "Under Review": "as-pill-blue",
  "In Planning": "as-pill-orange", "Awaiting Client Confirmation": "as-pill-orange",
  Confirmed: "as-pill-gold", "In Progress": "as-pill-gold",
  Completed: "as-pill-green", Cancelled: "as-pill-red", Draft: "as-pill-muted",
};

const CARDS = [
  { key: "active_trips", label: "Active Missions", icon: Plane },
  { key: "pending_requests", label: "Pending Requests", icon: Clock },
  { key: "in_planning", label: "In Planning", icon: Route },
  { key: "awaiting_confirmation", label: "Awaiting Client", icon: AlertCircle },
  { key: "tasks_due_today", label: "Tasks Due Today", icon: TrendingUp },
  { key: "concierge_pending", label: "Concierge Open", icon: Sparkles },
  { key: "new_inquiries", label: "New Charter Inquiries", icon: Users },
];

export default function OpsDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/ops").then(r => setStats(r.data)); }, []);
  return (
    <DashboardLayout role="ops">
      <div className="max-w-[1500px] mx-auto px-8 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="as-overline">Operations Command</div>
            <h1 className="as-serif text-5xl font-light mt-2" data-testid="ops-dashboard-title">Today at a glance.</h1>
          </div>
          <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{new Date().toUTCString().slice(0, 22)} UTC</div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {CARDS.map((c, i) => (
            <div key={c.key} className="as-card p-6 as-fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`kpi-${c.key}`}>
              <c.icon size={16} className="text-[#756E64] mb-4"/>
              <div className="as-serif text-5xl font-light text-[#1A1D24]">{stats?.[c.key] ?? "—"}</div>
              <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mt-3">{c.label}</div>
            </div>
          ))}
        </div>

        {/* Recent trips table */}
        <div className="as-card overflow-hidden" data-testid="recent-trips">
          <div className="flex items-center justify-between p-6 border-b border-[#E5DFD2]">
            <div className="as-overline">Recently Updated Missions</div>
            <Link to="/ops/trips" className="as-mono text-[10px] uppercase tracking-widest text-[#A47E2A] hover:underline">All trips ↗</Link>
          </div>
          <table className="w-full">
            <thead>
              <tr className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] text-left">
                <th className="px-6 py-3">Route</th>
                <th className="px-6 py-3">Client</th>
                <th className="px-6 py-3">Aircraft</th>
                <th className="px-6 py-3">Owner</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Updated</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recent_trips?.map(t => (
                <tr key={t.id} className="border-t border-[#E5DFD2] hover:bg-white transition-colors">
                  <td className="px-6 py-4">
                    <Link to={`/ops/trips/${t.id}`} className="as-serif text-lg hover:as-gold" data-testid={`recent-trip-${t.id}`}>
                      {t.legs?.[0]?.departure_airport} <span className="as-gold">→</span> {t.legs?.[0]?.arrival_airport}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm">{t.client_name}</td>
                  <td className="px-6 py-4 text-sm as-mono">{t.aircraft_tail || "—"}</td>
                  <td className="px-6 py-4 text-sm">{t.owner_name || "—"}</td>
                  <td className="px-6 py-4"><div className={`as-pill ${STATUS_PILL[t.status]} inline-block`}>{t.status}</div></td>
                  <td className="px-6 py-4 text-right as-mono text-xs text-[#756E64]">{new Date(t.updated_at).toUTCString().slice(5, 22)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
